import type { Workout, WorkoutExerciseEntry } from "./schemas/workout.js";

// Heuristic for "what did I last do for this exercise that's worth
// repeating today". The athlete picks an exercise on a fresh entry; we
// look back across saved workouts and surface the matching entry the
// logger should prefill from.
//
// Selection rule (B3/B4 spec): among the workouts that contain this
// slug, prefer the one whose heaviest weighted set was at ≥ 10 reps —
// the "heavier-at-≥-10-reps wins" rule that matches how a
// strength-focused user typically structures hypertrophy work. Falls
// back to the most recent matching workout if no entry hits the rep
// floor.
//
// Tiebreaker: recency (newer wins). A weighted set of equal weight in
// an older workout never beats a fresher one once the rep floor is
// satisfied.
//
// Returns the WorkoutExerciseEntry (so the caller has the sets array
// directly) plus the source workout's startedAt for "Prefill from
// <date>?" UI copy. Null when no past workout contains the slug.

export type RecentForExercise = {
  entry: WorkoutExerciseEntry;
  startedAt: string;
};

/**
 * The heaviest weighted set of one entry, with its reps.
 *
 * One definition for a question two features ask: the history prefill wants
 * "how much was loaded here" (above a rep floor, so a heavy triple does not
 * seed a set of ten), and EQ-1's machine tray wants the same number as the
 * signature that tells two machines apart. They read the same sets off the
 * same type, and a second hand-rolled loop is how the two would eventually
 * disagree.
 *
 * A fail counts like any other set (AP-0p model): reps are the clean ones
 * done, and `fail` is a pure label, so it is not an exclusion.
 */
export const heaviestWeightedSet = (
  entry: WorkoutExerciseEntry,
  opts: { minReps?: number } = {},
): { weightKg: number; reps: number } | undefined => {
  const minReps = opts.minReps ?? 0;
  let best: { weightKg: number; reps: number } | undefined;
  for (const s of entry.sets) {
    if (s.type !== "weighted") continue;
    if (s.reps < minReps) continue;
    if (best === undefined || s.weight > best.weightKg) {
      best = { weightKg: s.weight, reps: s.reps };
    }
  }
  return best;
};

export const pickRecentForExercise = (
  workouts: readonly Workout[],
  exerciseId: string,
): RecentForExercise | null => {
  const REP_FLOOR = 10;
  // Walk newest-first so the recency tiebreaker collapses naturally:
  // the first entry that beats the running best wins. Strict-greater
  // means equal-weight older entries don't displace newer ones.
  const ordered = [...workouts].sort((a, b) =>
    b.startedAt.localeCompare(a.startedAt),
  );
  let bestAtFloor: RecentForExercise | null = null;
  let bestAtFloorWeight = 0;
  let mostRecent: RecentForExercise | null = null;
  for (const w of ordered) {
    for (const entry of w.entries) {
      if (entry.exerciseId !== exerciseId) continue;
      if (!mostRecent) mostRecent = { entry, startedAt: w.startedAt };
      const heaviest =
        heaviestWeightedSet(entry, { minReps: REP_FLOOR })?.weightKg ?? 0;
      if (heaviest > bestAtFloorWeight) {
        bestAtFloor = { entry, startedAt: w.startedAt };
        bestAtFloorWeight = heaviest;
      }
      // First match in this workout is enough — multiple entries with
      // the same slug in one workout (drop-in extra round, A-B-A
      // alternation) usually share a load profile; the first appearance
      // is fine for prefill.
      break;
    }
  }
  return bestAtFloor ?? mostRecent;
};
